/** Contacts — v1 parity (contacts.js): search + type/source/tag filters,
 * cards, detail drawer, duplicate finder — and since the Phase 11 door:
 * Add / Edit / Delete (typed confirm + tombstone) / Merge, all synced to v1
 * through rec_contacts. */
import { useMemo, useState } from "react";
import type { Thread } from "../inbox/api";
import { C_PROF, C_SRC, C_TAGOPTS, clabel, type Contact } from "./api";
import { ContactDrawer } from "./ContactDrawer";
import { ContactModal } from "./ContactModal";
import { ConfirmDelete } from "../common/ConfirmDelete";
import { DupesPanel } from "./DupesPanel";

/** v1 renders 150 cards then "Show more" (leads-list.js renderPropList cap —
 * same medicine here: Tom has 3,200+ contacts and an uncapped grid jams the
 * phone). Filters/search still run over ALL contacts. */
const CAP_START = 150;
const CAP_STEP = 300;

export function ContactsPage({
  rows,
  note,
  threads,
  dealAddresses,
  edits,
  onOpenThread,
  onSave,
  onDelete,
  onMerge,
}: {
  rows: Contact[];
  note: string | null;
  threads: Thread[];
  dealAddresses: string[];
  edits: Map<string, Record<string, unknown>>;
  onOpenThread: ((peer: string) => void) | null;
  /** Phase 11 door mutations (null while the door probe hasn't confirmed) */
  onSave: ((rec: Record<string, unknown>, oldKey: string | null) => void) | null;
  onDelete: ((c: Contact) => void) | null;
  onMerge: ((keeper: Contact, losers: Contact[]) => void) | null;
}) {
  const [q, setQ] = useState("");
  const [type, setType] = useState("all");
  const [src, setSrc] = useState("all");
  const [tag, setTag] = useState("all");
  const [cap, setCap] = useState(CAP_START);
  const [viewing, setViewing] = useState<Contact | null>(null);
  const [dupes, setDupes] = useState(false);
  const [editing, setEditing] = useState<Contact | null>(null);
  const [adding, setAdding] = useState(false);
  const [deleting, setDeleting] = useState<Contact | null>(null);
  /** phone: the three selects live in the bottom sheet (option A) — the row
   *  of selects overflowed with no way to scroll, and + Add Contact sat
   *  offscreen past them */
  const [filtersOpen, setFiltersOpen] = useState(false);
  const activeFilters = (type !== "all" ? 1 : 0) + (src !== "all" ? 1 : 0) + (tag !== "all" ? 1 : 0);

  const types = useMemo(
    () => ["all", ...new Set(rows.map((c) => c.type).filter((t): t is string => !!t))],
    [rows],
  );
  const reset = () => setCap(CAP_START);

  const filtered = useMemo(() => {
    const needle = q.trim().toLowerCase();
    return rows.filter((c) => {
      if (type !== "all" && c.type !== type) return false;
      if (src !== "all" && (c.sourceCode ?? "") !== src) return false;
      if (tag !== "all" && !c.tags.includes(tag)) return false;
      if (!needle) return true;
      // v1 getContacts: n + co + cType + email + phone
      return [c.name, c.company, c.type, c.email, c.phone]
        .some((v) => (v ?? "").toLowerCase().includes(needle));
    });
  }, [rows, q, type, src, tag]);

  if (rows.length === 0) {
    return (
      <div className="contacts">
        <div className="empty gate">
          <strong>No contacts to show here yet.</strong>
          <span>
            Your contacts live in v1 and are safe. If you have contacts there and expect them
            here, v2 needs read access — run <code>MIGRATION_CRM2_PHASE6_READS.sql</code> in
            Supabase (read-only; it can't change anything).
          </span>
          {onSave ? <button className="btn primary" style={{ marginTop: 12 }} onClick={() => setAdding(true)}>+ Add Contact</button> : null}
        </div>
        {adding && onSave ? (
          <ContactModal
            existing={null}
            onClose={() => setAdding(false)}
            onSave={(rec, oldKey) => { onSave(rec, oldKey); setAdding(false); }}
          />
        ) : null}
      </div>
    );
  }

  const initials = (n: string) =>
    n.split(/\s+/).map((x) => x[0] ?? "").join("").slice(0, 2).toUpperCase() || "?";

  return (
    <div className="contacts">
      <header className="bar">
        <input
          placeholder="Search name, company, email, phone…"
          value={q}
          onChange={(e) => {
            setQ(e.target.value);
            reset();
          }}
        />
        {/* Phone (option A): the selects move into the sheet below; this
            button is what opens it. Desktop: the button doesn't exist and
            .barfilters is display:contents, so the selects render inline
            exactly as before. Same pattern as Properties and Tasks. */}
        <button
          type="button"
          className={"barfilterbtn" + (activeFilters ? " on" : "")}
          aria-expanded={filtersOpen}
          onClick={() => setFiltersOpen(true)}
        >
          Filters{activeFilters ? <span className="barfiltercount">{activeFilters}</span> : null}
        </button>
        {filtersOpen ? <div className="barfilterscrim" onClick={() => setFiltersOpen(false)} /> : null}
        <div className={"barfilters" + (filtersOpen ? " open" : "")}>
          <div className="barfiltershd">
            <span>Filters</span>
            <button type="button" className="barfilterdone" onClick={() => setFiltersOpen(false)}>Done</button>
          </div>
        <select value={type} onChange={(e) => { setType(e.target.value); reset(); }}>
          {types.map((t) => (
            <option key={t} value={t}>{t === "all" ? "All types" : t}</option>
          ))}
        </select>
        <select value={src} onChange={(e) => { setSrc(e.target.value); reset(); }}>
          <option value="all">All sources</option>
          {C_SRC.map(([code, label]) => (
            <option key={code} value={code}>{label}</option>
          ))}
        </select>
        <select value={tag} onChange={(e) => { setTag(e.target.value); reset(); }}>
          <option value="all">All tags</option>
          {C_TAGOPTS.map((t) => (
            <option key={t} value={t}>{t}</option>
          ))}
        </select>
        <button className="btn finddupes" onClick={() => { setFiltersOpen(false); setDupes(true); }}>Find duplicates</button>
        {activeFilters ? (
          <button type="button" className="barfilterclear" onClick={() => { setType("all"); setSrc("all"); setTag("all"); reset(); }}>Clear all filters</button>
        ) : null}
        </div>
        {onSave ? (
          <button className="btn addcontact" aria-label="Add contact" onClick={() => setAdding(true)}>
            <span className="acfull">+ Add Contact</span><span className="acmini">＋</span>
          </button>
        ) : null}
        <span className="count">
          {filtered.length.toLocaleString()} contacts
          {note ? <em className="note"> · {note}</em> : null}
          {!onSave ? <em className="note"> · adding/editing stays in v1 for now</em> : null}
        </span>
      </header>
      <div className="contactgrid">
        {filtered.length === 0 ? <div className="empty">No contacts match.</div> : null}
        {filtered.slice(0, cap).map((c) => (
          <div key={c.natKey} className="contactcard click" onClick={() => setViewing(c)}>
            <div className="chead">
              <span className="cavatar">{initials(c.name)}</span>
              <div className="cwho">
                <strong>{c.name}</strong>
                {c.type ? <span className="ctype">{c.type}</span> : null}
              </div>
              {c.profCode ? (
                <span className={`cprof cprof-${c.profCode}`}>{clabel(C_PROF, c.profCode)}</span>
              ) : null}
            </div>
            <div className="cbody">
              {c.company ? (
                <span className="cline">
                  <span className="cicon" aria-hidden="true">🏢</span>
                  <span className="ctext">{c.company}</span>
                </span>
              ) : null}
              {c.phone ? (
                <a className="cline" href={`tel:${c.phone}`} onClick={(e) => e.stopPropagation()}>
                  <span className="cicon" aria-hidden="true">📞</span>
                  <span className="ctext">{c.phone}</span>
                </a>
              ) : null}
              {c.email ? (
                <a className="cline" href={`mailto:${c.email}`} onClick={(e) => e.stopPropagation()}>
                  <span className="cicon" aria-hidden="true">✉️</span>
                  <span className="ctext">{c.email}</span>
                </a>
              ) : null}
            </div>
            {c.tags.length ? (
              <div className="ctags">
                {c.tags.slice(0, 4).map((t) => (
                  <span key={t} className="ctag">{t}</span>
                ))}
              </div>
            ) : null}
          </div>
        ))}
        {filtered.length > cap ? (
          <button className="showmore" onClick={() => setCap(cap + CAP_STEP)}>
            Show more ({(filtered.length - cap).toLocaleString()} remaining)
          </button>
        ) : null}
      </div>

      {viewing ? (
        <ContactDrawer
          contact={viewing}
          threads={threads}
          dealAddresses={dealAddresses}
          edits={edits}
          onClose={() => setViewing(null)}
          onOpenThread={onOpenThread}
          onEdit={onSave ? (c) => { setViewing(null); setEditing(c); } : null}
          onDelete={onDelete ? (c) => { setViewing(null); setDeleting(c); } : null}
        />
      ) : null}
      {adding || editing ? (
        <ContactModal
          existing={editing}
          onClose={() => { setAdding(false); setEditing(null); }}
          onSave={(rec, oldKey) => {
            onSave?.(rec, oldKey);
            setAdding(false);
            setEditing(null);
          }}
        />
      ) : null}
      {deleting ? (
        <ConfirmDelete
          message={`Delete ${deleting.name || "this contact"}? It can't be re-imported by the automations.`}
          onCancel={() => setDeleting(null)}
          onConfirm={() => {
            onDelete?.(deleting);
            setDeleting(null);
          }}
        />
      ) : null}
      {dupes ? (
        <DupesPanel
          rows={rows}
          onClose={() => setDupes(false)}
          onOpen={(c) => {
            setDupes(false);
            setViewing(c);
          }}
          onMerge={onMerge}
        />
      ) : null}
    </div>
  );
}
